/**
 * 主題：亮色 / 深色。
 *
 * 顏色本身全部在 `@shared/ui-system` 的 `@theme`（亮色為 base）與
 * `[data-theme='dark']`（深色覆寫）。這支只做兩件事：記住使用者選了什麼，
 * 把結果寫到 `<html>` 上。
 *
 * **這支不負責首次繪製。**React 掛載時第一幀早就畫完了，只靠這裡會先閃一下
 * 錯的主題。首屏的套用由各 app 的 HTML 內嵌 script 負責（見 apps/diet/index.html），
 * 那段必須在解析 HTML 時同步執行，無法由模組提供。
 *
 * **為什麼用 `useSyncExternalStore` 而非 `useState` + `useEffect`。**
 * 偏好是**存在於 React 之外**的狀態（localStorage），而且會被本頁的其他訂閱者與
 * 其他分頁改動。用 effect 讀進來再 setState 除了觸發連鎖渲染
 * （react-hooks/set-state-in-effect），也還要自己處理這些外部變動的同步。
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * 主題。只有兩種，**沒有「跟隨系統」**。
 *
 * 刻意不提供跟隨系統：那會讓「使用者看到的主題」同時取決於本站的設定與裝置的
 * 設定，出問題時要查兩個地方，而使用者往往不知道自己選的是哪一種。兩態的模型是
 * 「你選什麼就是什麼」，沒有第二個變因。
 *
 * 未選擇過時的預設是 `light`——亮色是設計系統的 base，讓「沒設定過」與「CSS 的
 * 預設狀態」是同一件事：首屏不必等 JS 判斷就已經正確，內嵌 script 也只在使用者
 * 確實選過深色時才需要動 DOM。
 */
export type Theme = 'light' | 'dark';

/**
 * localStorage 的鍵名。
 *
 * ⚠️ 各 app 的首屏內嵌 script 也讀這個鍵，但 script 無法 import 模組，只能抄字面量。
 * 改這裡要一併改那些 script，否則使用者的選擇會在重新整理後失效——而且不會報錯，
 * 只是每次都退回預設值。
 */
export const THEME_STORAGE_KEY = 'theme-preference';

/* ─── 偏好（localStorage）───────────────────────────────────────────────── */

/**
 * 同一分頁內的訂閱者。
 *
 * `storage` 事件只在**其他**分頁寫入時觸發，本頁自己呼叫 `setPreference` 不會收到，
 * 故另外維護一份清單並在寫入後主動通知。少了它，切換不會反映到畫面上。
 */
const listeners = new Set<() => void>();

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  /* 跨分頁同步：在另一個分頁改了外觀，這裡也跟著變 */
  window.addEventListener('storage', onChange);

  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onChange);
  };
}

/**
 * 讀取已儲存的主題；沒有或值不合法（含舊版的 `system`）時回 `light`（設計系統的 base）。
 */
function getStored(): Theme {
  /* Safari 無痕模式存取 localStorage 會拋例外，讀不到就當作沒設定過 */
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'light';
  } catch {
    return 'light';
  }
}

/**
 * 無瀏覽器環境（Storybook 的靜態渲染、日後若接上 SSR）的值。
 *
 * 與未設定過時的預設相同，也與 CSS 的 base 一致——三者對齊，任何情境下的首次
 * 渲染都是同一個主題，不會中途跳一下。
 */
function getServerStored(): Theme {
  return 'light';
}

/* ─── 套用 ──────────────────────────────────────────────────────────────── */

/**
 * 套用到 `<html>`。
 *
 * 亮色是 CSS 的 base，故套用亮色時**移除屬性**而非寫入 `light`——讓「沒有主題機制」
 * 與「明確選擇亮色」在 DOM 上呈現同一種狀態，少一種要考慮的組合。
 */
function apply(theme: Theme): void {
  const root = document.documentElement;

  if (theme === 'dark') {
    root.setAttribute('data-theme', 'dark');
  } else {
    root.removeAttribute('data-theme');
  }

  applyThemeColor(theme);
}

/**
 * 同步 `<meta name="theme-color">`。
 *
 * 這個值決定 iOS/Android 上瀏覽器工具列與 PWA 狀態列的顏色。不更新的話，切到深色後
 * 狀態列仍停在米白——安裝成 PWA 時最明顯，畫面頂端會多出一條與 app 無關的亮色帶。
 *
 * 標籤本身由各 app 的 PWA 層注入（`@shared/pwa` 的規格），這裡只改它的值，不新增。
 * 找不到就跳過：沒有 PWA 外殼的頁面本來就沒有這個標籤。
 *
 * ⚠️ 色值鏡射 `@theme` 的 `--color-background`，與 `@shared/pwa` 的 pwa-colors.ts
 * 同源。JS 讀不到 CSS 變數，故只能抄——改 background token 時三處要一起改。
 */
function applyThemeColor(theme: Theme): void {
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', theme === 'dark' ? '#0A0A0F' : '#FAFAF8');
}

export interface UseThemeReturn {
  readonly theme: Theme;
  readonly setTheme: (theme: Theme) => void;
}

export function useTheme(): UseThemeReturn {
  const theme = useSyncExternalStore(subscribe, getStored, getServerStored);

  /*
   * 把結果寫進 DOM。
   *
   * 這是**副作用而非狀態同步**，故留在 effect 裡是對的：DOM 屬性不是 React 管的資料。
   * 首次執行通常是無操作——內嵌 script 已在解析 HTML 時套過同樣的值。
   */
  useEffect(() => {
    apply(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* 無痕模式下存不進去。當次仍然生效，只是不會被記住——比整個切換失效好 */
    }
    /* 本頁不會收到自己觸發的 storage 事件，主動通知訂閱者 */
    listeners.forEach((listener) => listener());
  }, []);

  return { theme, setTheme };
}
